import { api } from './api';

export interface ProposedSchema {
  sheet_name: string;
  account_code_column: string;
  period_columns: { column: string; period_start: string; period_end: string; scenario: string }[];
  /** Part 14: wide payroll register — each amount column maps to a fixed account
   *  code (one sheet-wide period in period_start/period_end). Inverse of a TB. */
  account_columns?: { column: string; account_code: string }[];
  period_start?: string;
  period_end?: string;
  dimensions: Record<string, string>;
  scenario_default: string;
  currency_guess: string | null;
  unit_guess: '1' | '1000' | '1000000' | null;
  confidence: number;
}

export interface SheetPreview {
  name: string;
  rowCount: number;
  columnCount: number;
  rows: string[][];
}

export interface DataSource {
  id: string;
  workspaceId: string;
  kind: string;
  displayName: string;
  status: string;
  statusMessage: string | null;
  config: Record<string, unknown>;
  lastSyncedAt: string | null;
  createdAt: string;
}

export interface UploadResult {
  dataSource: DataSource;
  preview: { format: 'xlsx' | 'csv'; sheets: SheetPreview[] };
  schema: {
    proposedSchema: ProposedSchema | null;
    rawResponse: string;
    costUsd: number;
    ranHeuristicFallback: boolean;
  };
}

export interface IngestResult {
  factsInserted: number;
  periodsCovered: { start: string; end: string }[];
  accountCodes: number;
  scenarios: string[];
  accountsUpserted: number;
}

export interface AllocationRunSummary {
  ruleId: string;
  ruleName: string;
  factsInserted: number;
  status: 'ran' | 'error';
  reason?: string;
}

export interface ApplySchemaResult {
  dataSource: DataSource;
  ingest: IngestResult;
  allocationsRun?: AllocationRunSummary[];
}

export const dataSourcesApi = {
  async list(workspaceId: string): Promise<DataSource[]> {
    const r = await api.get<DataSource[]>('/api/data-sources', { params: { workspaceId } });
    return r.data;
  },

  async upload(workspaceId: string, file: File, vendor?: string): Promise<UploadResult> {
    const form = new FormData();
    form.append('file', file);
    // Let the browser set Content-Type so the multipart boundary is included.
    // Manually setting 'multipart/form-data' here strips the boundary on some
    // axios builds, producing "Multipart: Boundary not found" 400s.
    const r = await api.post<UploadResult>('/api/data-sources/upload', form, {
      params: { workspaceId, ...(vendor ? { vendor } : {}) },
    });
    return r.data;
  },

  /** JP-ERP vendor presets for the upload picker (Part 10). */
  async vendorPresets(): Promise<{ id: string; label: string; region: string }[]> {
    return (await api.get('/api/data-sources/vendor-presets')).data;
  },

  async applySchema(
    dataSourceId: string,
    schema: ProposedSchema,
    opts?: { autoRunAllocations?: boolean; importMappingId?: string },
  ): Promise<ApplySchemaResult> {
    const r = await api.post<ApplySchemaResult>(`/api/data-sources/${dataSourceId}/apply-schema`, {
      schema, autoRunAllocations: opts?.autoRunAllocations, importMappingId: opts?.importMappingId,
    });
    return r.data;
  },

  /** Delete a data source. Ingested facts are preserved (just unlinked). */
  async remove(dataSourceId: string): Promise<{ id: string; factsUnlinked: number }> {
    const r = await api.delete<{ id: string; factsUnlinked: number }>(`/api/data-sources/${dataSourceId}`);
    return r.data;
  },
};
